import { useState, useCallback, useEffect } from 'react';
import FormHeader from './components/FormHeader';
import Instrucciones from './components/Instrucciones';
import DatosPersonalesSection from './components/DatosPersonalesSection';
import CargoYProfesionSection from './components/CargoYProfesionSection';
import PublicacionesSection from './components/PublicacionesSection';
import DescargaExcelSection from './components/DescargaExcelSection';
import MisPublicaciones from './components/MisPublicaciones';
import AuthModal from './components/AuthModal';
import EncuestaSatisfaccion, { RespuestasEncuesta } from './components/EncuestaSatisfaccion';
import { FormData, Publicacion } from './types';
import { saveInvestigador } from './utils/storage';
import { saveEncuesta } from './utils/encuesta';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

const ADMIN_EMAIL = 'rortega@upal.edu';

const FORM_URL = 'https://readdy.ai/api/form/d6t92vcj8d20juc8vvag';

const initialForm: FormData = {
  nombre: '',
  apellido: '',
  email: '',
  telefono: '',
  orcid: '',
  unidad: '',
  postgrado: '',
  tipoUsuario: '',
  cargo: '',
  carreraDocente: '',
  materiaDocente: '',
  cargoAdminDetalle: '',
  carreraEstudiante: '',
  nivelEstudiante: '',
  publicaciones: [{ id: '1', titulo: '', link: '', baseDatos: '', baseDatosOtra: '', archivoNombre: '', archivoBase64: '', archivoFile: null }],
};

type FieldErrors = Partial<Record<keyof FormData, string>>;
type PubErrors = Record<string, Record<string, string>>;

export default function Home() {
  const { user, loading: authLoading } = useAuth();

  const [form, setForm] = useState<FormData>(initialForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [pubErrors, setPubErrors] = useState<PubErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [archivoWarnings, setArchivoWarnings] = useState<Record<number, string>>({});
  const [historialRefreshKey, setHistorialRefreshKey] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  // Encuesta de satisfacción
  const [showEncuesta, setShowEncuesta] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  const [authModalTab, setAuthModalTab] = useState<'login' | 'register'>('login');
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Pre-rellenar email con el del usuario autenticado
  useEffect(() => {
    if (user?.email) {
      setForm((prev) => ({ ...prev, email: user.email! }));
    }
  }, [user]);

  const handleChange = useCallback(
    (field: keyof FormData, value: string) => {
      setForm((prev) => {
        const updated = { ...prev, [field]: value };
        // Derivar tipoUsuario automáticamente desde cargo
        if (field === 'cargo') {
          updated.tipoUsuario = value === 'Estudiante' ? 'estudiante' : 'docente';
        }
        return updated;
      });
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const handleAddPublicacion = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      publicaciones: [
        ...prev.publicaciones,
        { id: Date.now().toString(), titulo: '', link: '', baseDatos: '', baseDatosOtra: '', archivoNombre: '', archivoBase64: '', archivoFile: null },
      ],
    }));
  }, []);

  const handleRemovePublicacion = useCallback((id: string) => {
    setForm((prev) => ({
      ...prev,
      publicaciones: prev.publicaciones.filter((p) => p.id !== id),
    }));
    setPubErrors((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  }, []);

  const handlePubChange = useCallback(
    (id: string, field: keyof Omit<Publicacion, 'id'>, value: string) => {
      setForm((prev) => ({
        ...prev,
        publicaciones: prev.publicaciones.map((p) =>
          p.id === id ? { ...p, [field]: value } : p
        ),
      }));
      setPubErrors((prev) => ({
        ...prev,
        [id]: { ...prev[id], [field]: '' },
      }));
    },
    []
  );

  const handlePubFileChange = useCallback(
    (id: string, nombre: string, file: File | null) => {
      setForm((prev) => ({
        ...prev,
        publicaciones: prev.publicaciones.map((p) =>
          p.id === id
            ? { ...p, archivoNombre: nombre, archivoFile: file, archivoBase64: '' }
            : p
        ),
      }));
    },
    []
  );

  const validate = (): boolean => {
    const errors: FieldErrors = {};
    if (!form.nombre.trim()) errors.nombre = 'El nombre es requerido';
    if (!form.apellido.trim()) errors.apellido = 'El apellido es requerido';
    if (!form.email.trim()) errors.email = 'El correo es requerido';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = 'Ingrese un correo válido';
    if (form.orcid.trim() && !/^https?:\/\/orcid\.org\/\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/.test(form.orcid.trim())) {
      errors.orcid = 'Ingrese una URL ORCID válida (ej. https://orcid.org/0000-0001-8702-3405)';
    }
    if (!form.unidad.trim()) errors.unidad = 'La profesión es requerida';
    if (!form.cargo.trim()) errors.cargo = 'El rol en la universidad es requerido';
    if (form.cargo === 'Docente') {
      if (!form.carreraDocente.trim()) errors.carreraDocente = 'Indique la carrera en la que imparte clases';
      if (!form.materiaDocente.trim()) errors.materiaDocente = 'Indique la materia o materias que imparte';
    }
    if (form.cargo === 'Administrativo' && !form.cargoAdminDetalle.trim()) {
      errors.cargoAdminDetalle = 'Indique la denominación de su cargo';
    }
    if (form.cargo === 'Estudiante') {
      if (!form.carreraEstudiante.trim()) errors.carreraEstudiante = 'La carrera es requerida';
      if (!form.nivelEstudiante.trim()) errors.nivelEstudiante = 'El año o nivel es requerido';
    }

    // Las publicaciones son opcionales — solo validar título si el usuario
    // escribió algo en otros campos de esa publicación (base de datos, link o archivo)
    const pErrors: PubErrors = {};
    form.publicaciones.forEach((pub) => {
      const tieneContenido =
        pub.baseDatos.trim() ||
        pub.link.trim() ||
        pub.archivoNombre.trim();
      if (tieneContenido && !pub.titulo.trim()) {
        pErrors[pub.id] = { titulo: 'Si completa datos de la publicación, el título es requerido' };
      }
    });

    setFieldErrors(errors);
    setPubErrors(pErrors);

    return Object.keys(errors).length === 0 && Object.keys(pErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Verificar autenticación antes de procesar
    if (!user) {
      setAuthModalTab('login');
      setShowAuthModal(true);
      return;
    }

    if (!validate()) {
      window.scrollTo({ top: 300, behavior: 'smooth' });
      return;
    }

    // Mostrar encuesta de satisfacción antes de enviar
    setShowEncuesta(true);
    setPendingSubmit(true);
    return;
  };

  const processSubmit = async (respuestasEncuesta?: RespuestasEncuesta) => {
    setShowEncuesta(false);
    setPendingSubmit(false);
    setSubmitting(true);
    setSubmitError('');
    setArchivoWarnings({});

    // Guardar encuesta de satisfacción si fue respondida
    if (respuestasEncuesta && user?.email) {
      try {
        await saveEncuesta(user.email, respuestasEncuesta);
      } catch {
        // No bloquear el registro si la encuesta falla
        console.warn('[Encuesta] No se pudo guardar la encuesta');
      }
    }

    // Verificar si hay archivos adjuntos para informar al usuario
    const tieneArchivos = form.publicaciones.some((p) => p.archivoFile instanceof File);

    const publicacionesTexto = form.publicaciones
      .map((p, i) => `[${i + 1}] ${p.titulo}${p.link ? ' — ' + p.link : ''}`)
      .join('\n');

    const bodyParams = new URLSearchParams({
      nombre: form.nombre,
      apellido: form.apellido,
      email: form.email,
      telefono: form.telefono,
      orcid: form.orcid,
      profesion_carrera: form.unidad,
      postgrado: form.postgrado,
      tipo_usuario: form.tipoUsuario,
      cargo: form.cargo,
      carrera_docente: form.carreraDocente,
      materia_docente: form.materiaDocente,
      cargo_admin_detalle: form.cargoAdminDetalle,
      carrera_estudiante: form.carreraEstudiante,
      nivel_estudiante: form.nivelEstudiante,
      publicaciones: publicacionesTexto,
    });

    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: bodyParams.toString(),
      });
    } catch {
      // silently continue — el registro principal es Supabase
    }

    // Si hay archivos, mostrar estado especial de "subiendo archivos"
    if (tieneArchivos) {
      setUploadingFiles(true);
      console.log('[Censo] Subiendo archivos adjuntos al Storage...');
    }

    let result;
    try {
      result = await saveInvestigador({
        id: Date.now().toString(),
        fechaRegistro: new Date().toLocaleDateString('es-BO'),
        nombre: form.nombre,
        apellido: form.apellido,
        email: form.email,
        telefono: form.telefono,
        orcid: form.orcid,
        unidad: form.unidad,
        postgrado: form.postgrado,
        tipoUsuario: form.tipoUsuario,
        cargo: form.cargo,
        carreraDocente: form.carreraDocente,
        materiaDocente: form.materiaDocente,
        cargoAdminDetalle: form.cargoAdminDetalle,
        carreraEstudiante: form.carreraEstudiante,
        nivelEstudiante: form.nivelEstudiante,
        publicaciones: form.publicaciones,
      });
    } catch (unexpectedErr) {
      // Garantizar que submitting se libere siempre, incluso ante errores inesperados
      setSubmitting(false);
      setUploadingFiles(false);
      setSubmitError(
        `Error inesperado al enviar el formulario: ${unexpectedErr instanceof Error ? unexpectedErr.message : String(unexpectedErr)}`
      );
      return;
    }

    setSubmitting(false);
    setUploadingFiles(false);

    if (!result.ok) {
      setSubmitError(
        result.error ??
        'Hubo un problema al guardar su registro en la base de datos. Por favor, intente nuevamente o contacte al administrador.'
      );
      return;
    }

    // Advertencias de archivos (el registro se guardó pero algún archivo no subió al Storage)
    if (result.archivoErrors && Object.keys(result.archivoErrors).length > 0) {
      setArchivoWarnings(result.archivoErrors);
    }

    setSubmitted(true);
    setHistorialRefreshKey((k) => k + 1);
    setForm({ ...initialForm, email: user.email ?? '', carreraDocente: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEncuestaSubmit = (respuestas: RespuestasEncuesta) => {
    processSubmit(respuestas);
  };

  const handleEncuestaSkip = () => {
    processSubmit(undefined);
  };

  const handleNewForm = () => {
    setSubmitted(false);
    setFieldErrors({});
    setPubErrors({});
    setArchivoWarnings({});
  };

  const handleUserLogout = async () => {
    await supabase.auth.signOut();
    setShowUserMenu(false);
    setForm(initialForm);
  };

  const openLogin = () => {
    setAuthModalTab('login');
    setShowAuthModal(true);
  };

  const openRegister = () => {
    setAuthModalTab('register');
    setShowAuthModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <FormHeader />

      {/* ── Barra de sesión de usuario ─────────────────────── */}
      {!authLoading && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-5xl mx-auto px-6 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`text-sm ${user ? 'ri-user-3-line text-emerald-600' : 'ri-user-line text-slate-400'}`} />
              </div>
              {user ? (
                <span className="text-xs text-slate-500">
                  Sesión activa como{' '}
                  <strong className="text-[#0D3157]">{user.email}</strong>
                </span>
              ) : (
                <span className="text-xs text-slate-400">
                  Inicie sesión para poder enviar el formulario
                </span>
              )}
            </div>

            {user ? (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowUserMenu((v) => !v)}
                  className="flex items-center gap-2 border border-slate-200 hover:border-slate-300 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center bg-[#0D3157] rounded-full">
                    <span className="text-white text-xs font-bold">
                      {user.email?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  Mi cuenta
                  {showUserMenu ? (
                    <i className="ri-arrow-up-s-line text-xs" />
                  ) : (
                    <i className="ri-arrow-down-s-line text-xs" />
                  )}
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl overflow-hidden z-20 min-w-[180px]">
                    <div className="px-4 py-3 border-b border-slate-100">
                      <p className="text-xs text-slate-400">Conectado como</p>
                      <p className="text-xs font-semibold text-[#0D3157] truncate max-w-[160px]">
                        {user.email}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleUserLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-r-line" />
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={openLogin}
                  className="flex items-center gap-1.5 border border-slate-300 hover:border-[#00426A] px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-[#0D3157] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-login-circle-line" />
                  Iniciar sesión
                </button>
                <button
                  type="button"
                  onClick={openRegister}
                  className="flex items-center gap-1.5 bg-[#00426A] hover:bg-[#0a2444] px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-user-add-line" />
                  Crear cuenta
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {submitted ? (
        <>
          <div className="max-w-5xl mx-auto px-6 mt-10">
            <div className="bg-white rounded-xl border border-emerald-200 p-8 flex flex-col md:flex-row items-center gap-6">
              <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                <i className="ri-checkbox-circle-line text-emerald-600 text-3xl" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-xl font-bold text-[#0D3157] mb-1">¡Registro enviado correctamente!</h2>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Sus publicaciones quedaron guardadas. Puede agregar más registros o revisar su historial completo a continuación.
                </p>
              </div>
              <button
                type="button"
                onClick={handleNewForm}
                className="flex items-center gap-2 bg-[#0D3157] hover:bg-[#0a2444] text-white font-semibold text-sm px-6 py-3 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
              >
                <i className="ri-add-circle-line" />
                Agregar otro registro
              </button>
            </div>
          </div>
          {/* Advertencias de archivos que no se pudieron subir al bucket CENSO */}
          {Object.keys(archivoWarnings).length > 0 && (
            <div className="max-w-5xl mx-auto px-6 mt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-amber-500 text-base" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-800">
                      El registro se guardó, pero algunos archivos no se subieron al Storage
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Para que los archivos funcionen, asegúrese de que el bucket <strong>CENSO</strong> esté creado y sea público en Supabase.
                    </p>
                  </div>
                </div>
                <ul className="space-y-1 ml-8">
                  {Object.entries(archivoWarnings).map(([idx, msg]) => (
                    <li key={idx} className="text-xs text-amber-700 flex items-start gap-2">
                      <i className="ri-file-warning-line flex-shrink-0 mt-0.5" />
                      <span><strong>Publicación {Number(idx) + 1}:</strong> {msg}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 ml-8 p-3 bg-amber-100 rounded-lg border border-amber-200">
                  <p className="text-xs font-bold text-amber-800 mb-1">Pasos para solucionarlo:</p>
                  <ol className="text-xs text-amber-700 space-y-0.5 list-decimal list-inside">
                    <li>En Supabase → Storage → New bucket → Nombre: <code className="bg-amber-200 px-1 rounded">CENSO</code> → marcar como <strong>Public</strong></li>
                    <li>En Storage → Policies → agregar política <code className="bg-amber-200 px-1 rounded">INSERT</code> para usuarios autenticados</li>
                    <li>En Database → tabla <code className="bg-amber-200 px-1 rounded">publicaciones</code> → agregar columna <code className="bg-amber-200 px-1 rounded">archivo_url text</code></li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {user && (
            <MisPublicaciones
              email={user.email!}
              refreshKey={historialRefreshKey}
              isAdmin={isAdmin}
            />
          )}
        </>
      ) : (
        <form
          data-readdy-form
          id="censo-investigadores"
          onSubmit={handleSubmit}
          noValidate
        >
          <Instrucciones />
          <DatosPersonalesSection
            data={form}
            onChange={handleChange}
            errors={fieldErrors}
            emailReadOnly={!!user}
          />
          <CargoYProfesionSection
            data={form}
            onChange={handleChange}
            errors={fieldErrors}
          />
          <PublicacionesSection
            publicaciones={form.publicaciones}
            onAdd={handleAddPublicacion}
            onRemove={handleRemovePublicacion}
            onChange={handlePubChange}
            onFileChange={handlePubFileChange}
            errors={pubErrors}
          />

          {submitError && (
            <div className="max-w-5xl mx-auto px-6 mt-4">
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <i className="ri-error-warning-line text-red-500 text-base mt-0.5 shrink-0" />
                <p className="text-red-600 text-sm leading-relaxed">{submitError}</p>
              </div>
            </div>
          )}

          <div className="max-w-5xl mx-auto px-6 mt-6">
            {!user ? (
              /* Banner de login requerido */
              <div className="bg-[#0D3157]/5 border-2 border-dashed border-[#0D3157]/25 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0D3157]/10 rounded-full flex-shrink-0">
                    <i className="ri-lock-2-line text-[#0D3157] text-2xl" />
                  </div>
                  <div>
                    <p className="font-bold text-base text-[#0D3157]">Inicio de sesión requerido</p>
                    <p className="text-slate-500 text-sm mt-0.5">
                      Para enviar el formulario debe tener una cuenta activa
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <button
                    type="button"
                    onClick={openRegister}
                    className="flex items-center gap-2 border-2 border-[#0D3157] text-[#0D3157] font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-[#0D3157]/5 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-add-line" />
                    Crear cuenta
                  </button>
                  <button
                    type="button"
                    onClick={openLogin}
                    className="flex items-center gap-2 bg-[#0D3157] hover:bg-[#00426A] text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-login-circle-line" />
                    Iniciar sesión
                  </button>
                </div>
              </div>
            ) : (
              /* Botón enviar normal */
              <div className="bg-[#0D3157] rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-white text-center md:text-left">
                  <p className="font-bold text-base">¿Todo listo para enviar?</p>
                  {uploadingFiles ? (
                    <p className="text-amber-300 text-sm mt-0.5 flex items-center gap-1.5">
                      <i className="ri-upload-cloud-2-line" />
                      Subiendo archivo adjunto, por favor espere…
                    </p>
                  ) : (
                    <p className="text-white/70 text-sm mt-0.5">
                      Enviando como <span className="font-semibold text-white/90">{user.email}</span>
                    </p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 bg-white text-[#0D3157] font-bold text-sm px-8 py-3 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {uploadingFiles ? (
                    <>
                      <i className="ri-upload-cloud-2-line animate-pulse" />
                      Subiendo archivo…
                    </>
                  ) : submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Enviando…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-2-line" />
                      Enviar Registro
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </form>
      )}

      {/* Historial del usuario logueado (o vista completa para admin) */}
      {user && !submitted && (
        <MisPublicaciones
          email={user.email!}
          refreshKey={historialRefreshKey}
          isAdmin={isAdmin}
        />
      )}

      {/* Admin Panel — solo visible para rortega@upal.edu */}
      {isAdmin && (
        <div className="max-w-5xl mx-auto px-6 mt-10 mb-12">
          <div className="border-t border-slate-200 pt-8 mb-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <div className="w-1 h-6 bg-emerald-600 rounded-full" />
                <h2 className="text-lg font-bold text-[#0D3157]">
                  Panel de Administración
                </h2>
                <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
                  <i className="ri-shield-check-line text-xs" />
                  Administrador
                </span>
              </div>
              <button
                type="button"
                onClick={handleUserLogout}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-500 font-semibold border border-slate-200 hover:border-red-300 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-r-line" />
                Cerrar sesión
              </button>
            </div>
            <p className="text-slate-400 text-xs mt-2">
              Bienvenido, <strong className="text-[#0D3157]">{user?.email}</strong>. Aquí puede ver, gestionar y descargar los registros del censo.
            </p>
          </div>
          <DescargaExcelSection />
        </div>
      )}

      <footer className="bg-[#0D3157] mt-4 py-6">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <img
            src="https://static.readdy.ai/image/15adce3e38297225eba5a769409d1488/aa557116e21533bd8236cb99d49327c5.png"
            alt="UPAL"
            className="h-10 object-contain brightness-0 invert opacity-90"
          />
          <p className="text-white/50 text-xs text-center">
            © 2026 Universidad Privada Abierta Latinoamericana — Censo de
            Investigadores. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Modal de autenticación */}
      {showAuthModal && (
        <AuthModal
          defaultTab={authModalTab}
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => setShowAuthModal(false)}
        />
      )}

      {/* Cerrar menú usuario al hacer clic afuera */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Encuesta de satisfacción — aparece antes de enviar el registro */}
      {showEncuesta && pendingSubmit && (
        <EncuestaSatisfaccion
          onSubmit={handleEncuestaSubmit}
          onSkip={handleEncuestaSkip}
          submitting={submitting}
        />
      )}
    </div>
  );
}
